import { ROUTES } from '../../lib/constants';

/**
 * Data + small presentational pieces for the mobile drawer's "Page
 * sections" nav (the tool's 6 page tabs). Split out of
 * `MobileMenuDrawer.tsx` to keep that file under the 250-line cap —
 * this module owns the tab identities, colors, and icon wrapper;
 * `MobileMenuNavGroups.tsx` renders them.
 */

export type TabId = 'home' | 'map' | 'rankings' | 'detail' | 'compare' | 'providers';

export interface DrawerTab {
  readonly id: TabId;
  readonly to: string;
  readonly label: string;
  readonly icon: React.ReactNode;
}

export const DRAWER_TABS: ReadonlyArray<DrawerTab> = [
  {
    id: 'home',
    to: ROUTES.home,
    label: 'Home',
    icon: (
      <>
        <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
        <polyline points="9 21 9 12 15 12 15 21" />
      </>
    ),
  },
  {
    id: 'map',
    to: ROUTES.map,
    label: 'Map',
    icon: (
      <>
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
        <circle cx="12" cy="9" r="2.5" />
      </>
    ),
  },
  {
    id: 'rankings',
    to: ROUTES.rankings,
    label: 'Rankings',
    icon: (
      <>
        <rect x="2" y="10" width="5" height="12" rx="1" />
        <rect x="9.5" y="6" width="5" height="16" rx="1" />
        <rect x="17" y="2" width="5" height="20" rx="1" />
      </>
    ),
  },
  {
    id: 'detail',
    to: ROUTES.detail,
    label: 'Detail',
    icon: (
      <>
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <line x1="11" y1="8" x2="11" y2="14" />
        <line x1="8" y1="11" x2="14" y2="11" />
      </>
    ),
  },
  {
    id: 'compare',
    to: ROUTES.compare,
    label: 'Compare',
    icon: (
      <>
        <rect x="2" y="3" width="8" height="18" rx="1" />
        <rect x="14" y="3" width="8" height="18" rx="1" />
      </>
    ),
  },
  {
    id: 'providers',
    to: ROUTES.providers,
    label: 'Providers',
    icon: (
      <>
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
        <line x1="12" y1="12" x2="12" y2="16" />
        <line x1="10" y1="14" x2="14" y2="14" />
      </>
    ),
  },
];

// A11y (WCAG 1.4.3): unlike the desktop SectionNav (active text on white), the
// mobile drawer's active text sits on the accent's own /0.07 tint (see
// ACTIVE_BG), so blue and green2 (~4.36:1 there) use their AA-safe dark
// variants. gold-dark (darkened token), rose, teal-dark already clear 4.5:1.
export const ACTIVE_TEXT: Record<TabId, string> = {
  home: 'text-navy',
  map: 'text-gold-dark',
  rankings: 'text-blue-link',
  detail: 'text-rose',
  compare: 'text-green-dark',
  providers: 'text-teal-dark',
};

export const ACTIVE_BG: Record<TabId, string> = {
  home: 'bg-navy/[0.06]',
  map: 'bg-gold/[0.08]',
  rankings: 'bg-blue/[0.07]',
  detail: 'bg-rose/[0.06]',
  compare: 'bg-green2/[0.07]',
  providers: 'bg-teal-dark/[0.07]',
};
